import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, Paperclip, Share2, MessageCircle, Loader2, Euro,
    FileText, Download,
} from "lucide-react";
import { api } from "../lib/api";
import { SECTORS, SECTOR_LABELS, WIZARD_CONFIG } from "../data/wizardConfig";
import { useAuth } from "../context/AuthContext";
import {
    QuoteCommentsThread,
    QuoteTrackingPanel,
} from "../components/QuoteThreadAndTracking";

const STATUS = {
    nuovo: { label: "Nuovo", cls: "badge-new" },
    in_valutazione: {
        label: "In valutazione",
        cls: "inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-xs font-bold",
    },
    risposto: { label: "Preventivo ricevuto", cls: "badge-replied" },
};

export default function QuoteDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/quotes/${id}`)
            .then(({ data }) => setQuote(data))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading)
        return (
            <div className="py-12 flex items-center justify-center text-[#0C4650]">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    if (!quote) return <div className="text-center py-12">Preventivo non trovato.</div>;

    const sectorLabel = SECTOR_LABELS[quote.sector] || SECTORS[quote.sector]?.label;
    const steps = WIZARD_CONFIG[quote.sector] || [];

    const openWhatsApp = () => {
        if (!quote.offer) return;
        const lines = [
            `Ciao ${quote.client_name},`,
            ``,
            `ecco la proposta *${quote.offer.title}* per te:`,
            quote.offer.description,
            ``,
            quote.offer.monthly_price ? `💰 ${quote.offer.monthly_price}` : "",
            quote.offer.price ? `Totale: ${quote.offer.price}` : "",
            quote.offer.attachment_url ? `📎 ${quote.offer.attachment_url}` : "",
            ``,
            `Fammi sapere se vuoi procedere!`,
        ]
            .filter(Boolean)
            .join("\n");
        const phone = (quote.client_phone || "").replace(/[^\d+]/g, "");
        const url = phone
            ? `https://wa.me/${phone.replace("+", "")}?text=${encodeURIComponent(lines)}`
            : `https://wa.me/?text=${encodeURIComponent(lines)}`;
        window.open(url, "_blank");
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6" data-testid="quote-detail-page">
            <Link to="/richieste" className="btn-ghost" data-testid="back-to-list">
                <ArrowLeft className="w-4 h-4" /> Torna alle richieste
            </Link>

            {/* Header */}
            <div className="card p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div>
                        <div className="text-xs tracking-widest font-bold text-[#898A8D]">
                            {sectorLabel?.toUpperCase()}
                        </div>
                        <h1 className="mt-1 text-3xl font-black text-[#0C4650]">
                            {quote.client_name}
                        </h1>
                        <div className="text-sm text-gray-500 mt-1">
                            Creato il {new Date(quote.created_at).toLocaleString("it-IT")}
                        </div>
                    </div>
                    <span className={STATUS[quote.status].cls + " text-sm"}>
                        {STATUS[quote.status].label}
                    </span>
                </div>
                {(quote.client_phone || quote.client_email) && (
                    <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-700">
                        {quote.client_phone && (
                            <span className="px-3 py-1 bg-[#f7f7f5] rounded-full">
                                📞 {quote.client_phone}
                            </span>
                        )}
                        {quote.client_email && (
                            <span className="px-3 py-1 bg-[#f7f7f5] rounded-full">
                                ✉️ {quote.client_email}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Commission (read-only for agent) */}
            {quote.commission_total != null && (
                <div
                    className="card p-5 sm:p-6 flex items-center gap-4 bg-gradient-to-br from-[#C7FF2C]/25 to-white border-[#C7FF2C] border-2"
                    data-testid="quote-commission-box"
                >
                    <div className="w-12 h-12 rounded-xl bg-[#0C4650] text-[#C7FF2C] flex items-center justify-center shrink-0">
                        <Euro className="w-6 h-6" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] tracking-[0.28em] font-bold text-[#0C4650]">
                            PROVV. TOTALE
                        </div>
                        <div className="text-2xl sm:text-3xl font-black text-[#0C4650] leading-tight mt-0.5">
                            {new Intl.NumberFormat("it-IT", {
                                style: "currency",
                                currency: "EUR",
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(quote.commission_total)}
                        </div>
                    </div>
                </div>
            )}

            {/* Offer (if replied) */}
            {quote.offer ? (
                <div
                    className="card p-6 sm:p-8 border-[#C7FF2C] border-2"
                    data-testid="quote-offer-box"
                >
                    <div className="flex items-center gap-2 text-xs font-bold tracking-widest text-[#0C4650]">
                        <span className="w-2 h-2 rounded-full bg-[#C7FF2C]" />
                        OFFERTA DALLA SEDE
                    </div>
                    <h2 className="mt-2 text-2xl font-black text-[#0C4650]">
                        {quote.offer.title}
                    </h2>
                    <p className="mt-3 text-gray-700 whitespace-pre-line">
                        {quote.offer.description}
                    </p>

                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                        {quote.offer.monthly_price && (
                            <PriceBox label="Rata mensile" value={quote.offer.monthly_price} />
                        )}
                        {quote.offer.price && (
                            <PriceBox label="Prezzo / Importo" value={quote.offer.price} />
                        )}
                    </div>

                    {quote.offer.notes && (
                        <div className="mt-5 bg-[#f7f7f5] rounded-xl p-4 text-sm text-gray-700">
                            <div className="text-xs tracking-widest font-bold text-[#898A8D]">
                                NOTE
                            </div>
                            <div className="mt-1 whitespace-pre-line">{quote.offer.notes}</div>
                        </div>
                    )}

                    {quote.offer.attachment_url && (
                        <a
                            href={
                                quote.offer.attachment_url.includes("/api/files/")
                                    ? `${quote.offer.attachment_url}?token=${encodeURIComponent(localStorage.getItem("scintilla_token") || "")}`
                                    : quote.offer.attachment_url
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="mt-5 inline-flex items-center gap-2 text-[#0C4650] font-bold hover:underline"
                            data-testid="offer-attachment"
                        >
                            <Paperclip className="w-4 h-4" /> Scarica allegato
                        </a>
                    )}

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={openWhatsApp}
                            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-5 py-3 rounded-lg hover:-translate-y-0.5 transition-all"
                            data-testid="share-whatsapp"
                        >
                            <MessageCircle className="w-4 h-4" /> Invia su WhatsApp
                        </button>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                            }}
                            className="btn-outline"
                            data-testid="share-copy-link"
                        >
                            <Share2 className="w-4 h-4" /> Copia link
                        </button>
                    </div>
                </div>
            ) : (
                <div
                    className="card p-6 bg-[#f7f7f5] text-center"
                    data-testid="quote-waiting-offer"
                >
                    <p className="text-[#0C4650] font-semibold">
                        La sede sta preparando la tua offerta.
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                        Riceverai una notifica appena sarà disponibile.
                    </p>
                </div>
            )}

            {/* Tracking PDF (visite + conferma online) */}
            <QuoteTrackingPanel quoteId={id} />

            {/* Thread commenti agente <-> sede */}
            <QuoteCommentsThread quoteId={id} currentUser={user} />

            {/* Documenti cliente allegati (Energy Quoter) */}
            {Array.isArray(quote.answers?.client_documents)
                && quote.answers.client_documents.length > 0 && (
                <ClientDocumentsCard docs={quote.answers.client_documents} />
            )}

            {/* Answers summary */}
            <div className="card p-6 sm:p-8">
                <h3 className="text-lg font-bold text-[#0C4650]">Dettagli richiesta</h3>
                <div className="mt-4 space-y-4">
                    {steps.map((s, idx) => (
                        <div key={s.title || idx} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                            <div className="text-xs tracking-widest font-bold text-[#898A8D]">
                                {s.title.toUpperCase()}
                            </div>
                            <div className="mt-2 space-y-1.5">
                                {s.fields.map((f) => {
                                    const v = quote.answers?.[f.id];
                                    if (v == null || v === "" || (Array.isArray(v) && v.length === 0))
                                        return null;
                                    return (
                                        <div
                                            key={f.id}
                                            className="flex flex-col sm:flex-row sm:justify-between gap-1 text-sm"
                                        >
                                            <span className="text-gray-500">{f.label}</span>
                                            <span className="text-[#0C4650] font-semibold text-right">
                                                {Array.isArray(v) ? v.join(", ") : v}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const PriceBox = ({ label, value }) => (
    <div className="bg-[#0C4650] text-white rounded-xl p-4">
        <div className="text-[10px] tracking-widest text-[#91C6C0]">{label}</div>
        <div className="text-2xl font-black text-[#C7FF2C] mt-0.5">{value}</div>
    </div>
);

const ClientDocumentsCard = ({ docs }) => {
    const token = localStorage.getItem("scintilla_token") || "";
    const fmt = (b) =>
        b == null ? "" : b < 1024 ? `${b} B` : b < 1024 * 1024 ?
            `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;
    return (
        <div className="card p-6 sm:p-8" data-testid="client-docs-attached">
            <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0C4650]" />
                <h3 className="text-lg font-black text-[#0C4650]">
                    Documenti cliente allegati
                </h3>
                <span className="ml-auto text-xs font-bold text-[#898A8D]">
                    {docs.length} {docs.length === 1 ? "file" : "file"}
                </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
                Documentazione caricata dall'agente per l'attivazione dello switch.
            </p>
            <div className="mt-4 space-y-2">
                {docs.map((d) => (
                    <a key={d.file_id}
                       href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_id}?token=${encodeURIComponent(token)}`}
                       download={d.filename}
                       target="_blank"
                       rel="noreferrer"
                       data-testid={`attached-doc-${d.file_id}`}
                       className="flex items-center gap-3 p-3 rounded-xl bg-[#f7f7f5] hover:bg-[#C7FF2C]/20 border-2 border-transparent hover:border-[#C7FF2C] transition-all group">
                        <div className="w-9 h-9 rounded-lg bg-[#0C4650] text-[#C7FF2C] flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-[#0C4650] text-sm truncate">
                                {d.filename}
                            </div>
                            <div className="text-[11px] text-gray-500">
                                {fmt(d.size)}
                                {d.content_type ? ` · ${d.content_type}` : ""}
                            </div>
                        </div>
                        <Download className="w-4 h-4 text-[#0C4650] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                ))}
            </div>
        </div>
    );
};
